import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { giraffeRoot, giraffeTrip } from '@/lib/giraffePaths';
import { Trash2, Upload, Images, X } from 'lucide-react';
import { useConfirmation } from '../components/shared/ZCard';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export default function GiraffeSettings() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const feedback = useConfirmation();
  const [trip, setTrip] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFramedPicker, setShowFramedPicker] = useState(false);
  const [framedPhotos, setFramedPhotos] = useState([]);

  useEffect(() => {
    api.get(`/trips/${tripId}`).then(r => {
      setTrip(r.data);
      setForm({
        title: r.data.title || '',
        description: r.data.description || '',
        start_date: r.data.start_date || '',
        end_date: r.data.end_date || '',
        cover_image: r.data.cover_image || '',
        status: r.data.status || 'planning',
      });
    }).catch(() => navigate(giraffeRoot()));
  }, [tripId, navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/trips/${tripId}`, form);
      feedback.confirm();
      navigate(giraffeTrip(tripId));
    } catch { feedback.fail(); }
    finally { setSaving(false); }
  };

  const openFramedPicker = async () => {
    if (!framedPhotos.length) {
      try {
        const { data } = await api.get('/photos');
        setFramedPhotos(data);
      } catch { return; }
    }
    setShowFramedPicker(true);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/upload', formData);
      setForm(prev => ({ ...prev, cover_image: data.url }));
    } catch { feedback.fail(); }
  };

  const deleteTrip = async () => {
    try {
      await api.delete(`/trips/${tripId}`);
      feedback.confirm();
      navigate(giraffeRoot());
    } catch { feedback.fail(); }
  };

  const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  const handleStatusToggle = (completed) => {
    setForm(prev => ({
      ...prev,
      status: completed ? 'completed' : 'active',
    }));
  };

  if (!trip) return <div style={{ padding: '48px 24px' }}><ZLoadingBar accent="#10B981" loading={true} /></div>;

  const isCompleted = form.status === 'completed';

  return (
    <div className="p-6 md:p-12 max-w-2xl mx-auto" data-testid="giraffe-settings-page">
      <div style={{ height: 32, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)', marginBottom: 32 }}>
        <Breadcrumb>
          <BreadcrumbList style={{ gap: 0 }}>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={giraffeRoot()} style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                  Giraffe
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={giraffeTrip(tripId)} style={{ color: '#6b7084', fontSize: 13, fontWeight: 400, textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
                  {trip.title}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
            <BreadcrumbItem>
              <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                Settings
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <h1 className="text-3xl font-medium text-[#f0f0f5] font-serif mb-8">Trip Settings</h1>

      <div className="space-y-6">
        {/* Cover Image */}
        <div>
          <label className="text-sm text-[#b0b4c0] font-light mb-2 block">Cover Image</label>
          {form.cover_image && (
            <img src={getImageUrl(form.cover_image)} alt="" className="w-full h-40 object-cover rounded-xl mb-3" />
          )}
          <div className="flex gap-2">
            <label className="flex-1 flex items-center gap-2 px-4 py-3 bg-white/[0.05] border border-white/[0.06] rounded-lg cursor-pointer hover:border-[#6B35B8]/40 transition-colors">
              <Upload size={16} className="text-[#b0b4c0]" />
              <span className="text-sm text-[#b0b4c0]">Upload cover</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} data-testid="cover-upload" />
            </label>
            <button
              onClick={openFramedPicker}
              className="flex items-center gap-2 px-4 py-3 bg-white/[0.05] border border-white/[0.06] rounded-lg hover:border-[#6B35B8]/40 transition-colors"
              data-testid="pick-from-framed"
            >
              <Images size={16} className="text-[#b0b4c0]" />
              <span className="text-sm text-[#b0b4c0]">Framed</span>
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm text-[#b0b4c0] font-light mb-2 block">Trip Name</label>
          <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]" data-testid="settings-title" />
        </div>

        <div>
          <label className="text-sm text-[#b0b4c0] font-light mb-2 block">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] min-h-[80px]" data-testid="settings-desc" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[#b0b4c0] font-light mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]" data-testid="settings-start" />
          </div>
          <div>
            <label className="text-sm text-[#b0b4c0] font-light mb-2 block">End Date</label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]" data-testid="settings-end" />
          </div>
        </div>

        {/* Trip Status Panel */}
        <div className="border border-white/[0.06] rounded-xl p-4 space-y-4" data-testid="publishing-panel">
          <p className="text-xs font-mono tracking-wider uppercase text-[#b0b4c0]/70">Status</p>

          <div className="flex items-center justify-between py-2">
            <p className="text-sm text-[#f0f0f5]">Mark as completed</p>
            <Switch
              checked={isCompleted}
              onCheckedChange={handleStatusToggle}
              className="data-[state=checked]:bg-[#6B35B8]"
              data-testid="toggle-completed"
            />
          </div>
        </div>

        <Button onClick={save} disabled={saving || !form.title} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full" data-testid="settings-save">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>

        <button onClick={() => setShowDeleteConfirm(true)} className="w-full text-center text-red-400/60 hover:text-red-400 text-sm font-light py-2 transition-colors" data-testid="settings-delete">
          Delete Trip
        </button>
      </div>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteTrip}
        itemName={trip?.title || 'this trip'}
        itemType="trip"
      />

      {/* Framed Photo Picker */}
      <Dialog open={showFramedPicker} onOpenChange={setShowFramedPicker}>
        <DialogContent className="bg-white/[0.02] border-white/[0.06] max-w-2xl w-full p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
            <h2 className="text-[#f0f0f5] text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Pick from Framed</h2>
            <button onClick={() => setShowFramedPicker(false)} className="text-[#b0b4c0] hover:text-[#f0f0f5] transition-colors"><X size={18} /></button>
          </div>
          {framedPhotos.length === 0 ? (
            <div className="p-12 text-center text-[#b0b4c0] text-sm">No photos in Framed yet.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 p-2 max-h-[70vh] overflow-y-auto">
              {framedPhotos.map(photo => (
                <button
                  key={photo.id}
                  onClick={() => { setForm(prev => ({ ...prev, cover_image: photo.url })); setShowFramedPicker(false); }}
                  className="relative aspect-square overflow-hidden rounded group"
                >
                  <img
                    src={getImageUrl(photo.url)}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-[#6B35B8]/0 group-hover:bg-[#6B35B8]/20 transition-colors" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
